"use client";
import { Button, buttonVariants } from "./button";
import { Input } from "./input";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { cn } from "@repo/utils/utils";
import { add, format } from "date-fns";
import { enUS } from "date-fns/locale";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, X } from "lucide-react";
import { Clock } from "lucide-react";
import * as React from "react";
import { useImperativeHandle, useRef } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select";
import { DayPicker } from "react-day-picker";
// ---------- utils start ----------
/**
 * regular expression to check for valid hour format (01-23)
 */
function isValidHour(value) {
    return /^(0[0-9]|1[0-9]|2[0-3])$/.test(value);
}
/**
 * regular expression to check for valid 12 hour format (01-12)
 */
function isValid12Hour(value) {
    return /^(0[1-9]|1[0-2])$/.test(value);
}
/**
 * regular expression to check for valid minute format (00-59)
 */
function isValidMinuteOrSecond(value) {
    return /^[0-5][0-9]$/.test(value);
}
function getValidNumber(value, { max, min = 0, loop = false }) {
    let numericValue = parseInt(value, 10);
    if (!Number.isNaN(numericValue)) {
        if (!loop) {
            if (numericValue > max)
                numericValue = max;
            if (numericValue < min)
                numericValue = min;
        }
        else {
            if (numericValue > max)
                numericValue = min;
            if (numericValue < min)
                numericValue = max;
        }
        return numericValue.toString().padStart(2, "0");
    }
    return "00";
}
function getValidHour(value) {
    if (isValidHour(value))
        return value;
    return getValidNumber(value, { max: 23 });
}
function getValid12Hour(value) {
    if (isValid12Hour(value))
        return value;
    return getValidNumber(value, { min: 1, max: 12 });
}
function getValidMinuteOrSecond(value) {
    if (isValidMinuteOrSecond(value))
        return value;
    return getValidNumber(value, { max: 59 });
}
function getValidArrowNumber(value, { min, max, step }) {
    let numericValue = parseInt(value, 10);
    if (!Number.isNaN(numericValue)) {
        numericValue += step;
        return getValidNumber(String(numericValue), { min, max, loop: true });
    }
    return "00";
}
function getValidArrowHour(value, step) {
    return getValidArrowNumber(value, { min: 0, max: 23, step });
}
function getValidArrow12Hour(value, step) {
    return getValidArrowNumber(value, { min: 1, max: 12, step });
}
function getValidArrowMinuteOrSecond(value, step) {
    return getValidArrowNumber(value, { min: 0, max: 59, step });
}
function setMinutes(date, value) {
    const minutes = getValidMinuteOrSecond(value);
    date.setMinutes(parseInt(minutes, 10));
    return date;
}
function setSeconds(date, value) {
    const seconds = getValidMinuteOrSecond(value);
    date.setSeconds(parseInt(seconds, 10));
    return date;
}
function setHours(date, value) {
    const hours = getValidHour(value);
    date.setHours(parseInt(hours, 10));
    return date;
}
function set12Hours(date, value, period) {
    const hours = parseInt(getValid12Hour(value), 10);
    const convertedHours = convert12HourTo24Hour(hours, period);
    date.setHours(convertedHours);
    return date;
}
function setDateByType(date, value, type, period) {
    switch (type) {
        case "minutes":
            return setMinutes(date, value);
        case "seconds":
            return setSeconds(date, value);
        case "hours":
            return setHours(date, value);
        case "12hours": {
            if (!period)
                return date;
            return set12Hours(date, value, period);
        }
        default:
            return date;
    }
}
function getDateByType(date, type) {
    if (!date)
        return "00";
    switch (type) {
        case "minutes":
            return getValidMinuteOrSecond(String(date.getMinutes()));
        case "seconds":
            return getValidMinuteOrSecond(String(date.getSeconds()));
        case "hours":
            return getValidHour(String(date.getHours()));
        case "12hours":
            return getValid12Hour(String(display12HourValue(date.getHours())));
        default:
            return "00";
    }
}
function getArrowByType(value, step, type) {
    switch (type) {
        case "minutes":
            return getValidArrowMinuteOrSecond(value, step);
        case "seconds":
            return getValidArrowMinuteOrSecond(value, step);
        case "hours":
            return getValidArrowHour(value, step);
        case "12hours":
            return getValidArrow12Hour(value, step);
        default:
            return "00";
    }
}
/**
 * handles value change of 12-hour input
 * 12:00 PM is 12:00
 * 12:00 AM is 00:00
 */
function convert12HourTo24Hour(hour, period) {
    if (period === "PM") {
        if (hour <= 11) {
            return hour + 12;
        }
        return hour;
    }
    if (period === "AM") {
        if (hour === 12)
            return 0;
        return hour;
    }
    return hour;
}
/**
 * time is stored in the 24-hour form,
 * but needs to be displayed to the user
 * in its 12-hour representation
 */
function display12HourValue(hours) {
    if (hours === 0 || hours === 12)
        return "12";
    if (hours >= 22)
        return `${hours - 12}`;
    if (hours % 12 > 9)
        return `${hours}`;
    return `0${hours % 12}`;
}
function genMonths(locale) {
    return Array.from({ length: 12 }, (_, i) => ({
        value: i,
        label: format(new Date(2021, i), "MMMM", { locale }),
    }));
}
function genYears(yearRange = 50) {
    const today = new Date();
    return Array.from({ length: yearRange * 2 + 1 }, (_, i) => ({
        value: today.getFullYear() - yearRange + i,
        label: (today.getFullYear() - yearRange + i).toString(),
    }));
}
// ---------- utils end ----------
function Calendar({ className, classNames, showOutsideDays = true, yearRange = 50, ...props }) {
    const MONTHS = React.useMemo(() => {
        let locale = enUS;
        const { options, localize, formatLong } = props.locale || {};
        if (options && localize && formatLong) {
            locale = {
                options,
                localize,
                formatLong,
            };
        }
        return genMonths(locale);
    }, []);
    const YEARS = React.useMemo(() => genYears(yearRange), []);
    return (<DayPicker showOutsideDays={showOutsideDays} className={cn("p-3", className)} classNames={{
            months: "flex flex-col sm:flex-row space-y-4  sm:space-y-0 justify-center",
            month: "flex flex-col items-center space-y-4",
            month_caption: "flex justify-center pt-1 relative items-center",
            caption_label: "text-sm font-medium",
            nav: "space-x-1 flex items-center ",
            button_previous: cn(buttonVariants({ variant: "outline" }), "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 absolute left-5 top-5"),
            button_next: cn(buttonVariants({ variant: "outline" }), "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 absolute right-5 top-5"),
            month_grid: "w-full border-collapse space-y-1",
            weekdays: cn("flex", props.showWeekNumber && "justify-end"),
            weekday: "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
            week: "flex w-full mt-2",
            day: "h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-range-start)]:rounded-l-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
            day_button: cn(buttonVariants({ variant: "ghost" }), "h-9 w-9 p-0 font-normal aria-selected:opacity-100 rounded-l-md rounded-r-md"),
            range_start: "day-range-start",
            range_end: "day-range-end",
            selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground rounded-l-md rounded-r-md",
            today: "bg-accent text-accent-foreground",
            outside: "day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
            disabled: "text-muted-foreground opacity-50",
            range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
            hidden: "invisible",
            ...classNames,
        }} components={{
            Chevron: ({ ...props }) => props.orientation === "left" ? (<ChevronLeft className="h-4 w-4"/>) : (<ChevronRight className="h-4 w-4"/>),
            MonthCaption: ({ calendarMonth }) => {
                return (<div className="inline-flex gap-2">
              <Select value={calendarMonth.date.getMonth().toString()} onValueChange={(value) => {
                        const newDate = new Date(calendarMonth.date);
                        newDate.setMonth(Number.parseInt(value, 10));
                        props.onMonthChange?.(newDate);
                    }}>
                <SelectTrigger className="w-fit gap-1 border-none p-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS.map((month) => (<SelectItem key={month.value} value={month.value.toString()}>
                      {month.label}
                    </SelectItem>))}
                </SelectContent>
              </Select>
              <Select value={calendarMonth.date.getFullYear().toString()} onValueChange={(value) => {
                        const newDate = new Date(calendarMonth.date);
                        newDate.setFullYear(Number.parseInt(value, 10));
                        props.onMonthChange?.(newDate);
                    }}>
                <SelectTrigger className="w-fit gap-1 border-none p-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {YEARS.map((year) => (<SelectItem key={year.value} value={year.value.toString()}>
                      {year.label}
                    </SelectItem>))}
                </SelectContent>
              </Select>
            </div>);
            },
        }} {...props}/>);
}
Calendar.displayName = "Calendar";
const TimePeriodSelect = React.forwardRef(({ period, setPeriod, date, onDateChange, onLeftFocus, onRightFocus }, ref) => {
    const handleKeyDown = (e) => {
        if (e.key === "ArrowRight")
            onRightFocus?.();
        if (e.key === "ArrowLeft")
            onLeftFocus?.();
    };
    const handleValueChange = (value) => {
        setPeriod?.(value);
        /**
         * trigger an update whenever the user switches between AM and PM;
         * otherwise user must manually change the hour each time
         */
        if (date) {
            const tempDate = new Date(date);
            const hours = display12HourValue(date.getHours());
            onDateChange?.(setDateByType(tempDate, hours.toString(), "12hours", period === "AM" ? "PM" : "AM"));
        }
    };
    return (<div className="flex h-10 items-center">
        <Select defaultValue={period} onValueChange={(value) => handleValueChange(value)}>
          <SelectTrigger ref={ref} className="w-[65px] focus:bg-accent focus:text-accent-foreground" onKeyDown={handleKeyDown}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="AM">AM</SelectItem>
            <SelectItem value="PM">PM</SelectItem>
          </SelectContent>
        </Select>
      </div>);
});
TimePeriodSelect.displayName = "TimePeriodSelect";
const TimePickerInput = React.forwardRef(({ className, type = "tel", value, id, name, date = new Date(new Date().setHours(0, 0, 0, 0)), onDateChange, onChange, onKeyDown, picker, period, onLeftFocus, onRightFocus, ...props }, ref) => {
    const [flag, setFlag] = React.useState(false);
    const [prevIntKey, setPrevIntKey] = React.useState("0");
    /**
     * allow the user to enter the second digit within 2 seconds
     * otherwise start again with entering first digit
     */
    React.useEffect(() => {
        if (flag) {
            const timer = setTimeout(() => {
                setFlag(false);
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [flag]);
    const calculatedValue = React.useMemo(() => {
        return getDateByType(date, picker);
    }, [date, picker]);
    const calculateNewValue = (key) => {
        /*
         * If picker is '12hours' and the first digit is 0, then the second digit is automatically set to 1.
         * The second entered digit will break the condition and the value will be set to 10-12.
         */
        if (picker === "12hours") {
            if (flag && calculatedValue.slice(1, 2) === "1" && prevIntKey === "0")
                return `0${key}`;
        }
        return !flag ? `0${key}` : calculatedValue.slice(1, 2) + key;
    };
    const handleKeyDown = (e) => {
        if (e.key === "Tab")
            return;
        e.preventDefault();
        if (e.key === "ArrowRight")
            onRightFocus?.();
        if (e.key === "ArrowLeft")
            onLeftFocus?.();
        if (["ArrowUp", "ArrowDown"].includes(e.key)) {
            const step = e.key === "ArrowUp" ? 1 : -1;
            const newValue = getArrowByType(calculatedValue, step, picker);
            if (flag)
                setFlag(false);
            const tempDate = date ? new Date(date) : new Date();
            onDateChange?.(setDateByType(tempDate, newValue, picker, period));
        }
        if (e.key >= "0" && e.key <= "9") {
            if (picker === "12hours")
                setPrevIntKey(e.key);
            const newValue = calculateNewValue(e.key);
            if (flag)
                onRightFocus?.();
            setFlag((prev) => !prev);
            const tempDate = date ? new Date(date) : new Date();
            onDateChange?.(setDateByType(tempDate, newValue, picker, period));
        }
    };
    return (<Input ref={ref} id={id || picker} name={name || picker} className={cn("w-[48px] text-center font-mono text-base tabular-nums caret-transparent focus:bg-accent focus:text-accent-foreground [&::-webkit-inner-spin-button]:appearance-none", className)} value={value || calculatedValue} onChange={(e) => {
            e.preventDefault();
            onChange?.(e);
        }} type={type} inputMode="decimal" onKeyDown={(e) => {
            onKeyDown?.(e);
            handleKeyDown(e);
        }} {...props}/>);
});
TimePickerInput.displayName = "TimePickerInput";
const TimePicker = React.forwardRef(({ date, onChange, hourCycle = 24, granularity = "second" }, ref) => {
    const minuteRef = React.useRef(null);
    const hourRef = React.useRef(null);
    const secondRef = React.useRef(null);
    const periodRef = React.useRef(null);
    const [period, setPeriod] = React.useState(date && date.getHours() >= 12 ? "PM" : "AM");
    useImperativeHandle(ref, () => ({
        minuteRef: minuteRef.current,
        hourRef: hourRef.current,
        secondRef: secondRef.current,
        periodRef: periodRef.current,
    }), [minuteRef, hourRef, secondRef]);
    return (<div className="flex items-center justify-end gap-2">
        <label htmlFor="datetime-picker-hour-input" className="cursor-pointer">
          <Clock className="mr-2 h-4 w-4"/>
        </label>
        <TimePickerInput picker={hourCycle === 24 ? "hours" : "12hours"} date={date} id="datetime-picker-hour-input" onDateChange={onChange} ref={hourRef} period={period} onRightFocus={() => minuteRef?.current?.focus()}/>
        {(granularity === "minute" || granularity === "second") && (<>
            :
            <TimePickerInput picker="minutes" date={date} onDateChange={onChange} ref={minuteRef} onLeftFocus={() => hourRef?.current?.focus()} onRightFocus={() => secondRef?.current?.focus()}/>
          </>)}
        {granularity === "second" && (<>
            :
            <TimePickerInput picker="seconds" date={date} onDateChange={onChange} ref={secondRef} onLeftFocus={() => minuteRef?.current?.focus()} onRightFocus={() => periodRef?.current?.focus()}/>
          </>)}
        {hourCycle === 12 && (<div className="grid gap-1 text-center">
            <TimePeriodSelect period={period} setPeriod={setPeriod} date={date} onDateChange={(date) => {
                onChange?.(date);
                if (date && date?.getHours() >= 12) {
                    setPeriod("PM");
                }
                else {
                    setPeriod("AM");
                }
            }} ref={periodRef} onLeftFocus={() => secondRef?.current?.focus()}/>
          </div>)}
      </div>);
});
TimePicker.displayName = "TimePicker";
const DateTimePicker = React.forwardRef(({ locale = enUS, value, onChange, hourCycle = 24, yearRange = 200, disabled = false, displayFormat, granularity = "second", placeholder = "Pick a date", className, onSubmitDate, triggerRef, noPortal, clearable = false, allowExpression = false, expressionValue, onExpressionChange, renderExpressionEditor, variablePopupContainer, ...props }, ref) => {
    const [open, setOpen] = React.useState(false);
    const [draftValue, setDraftValue] = React.useState(value);
    const [month, setMonth] = React.useState(value ?? new Date());
    // A value is either a fixed date or a variable expression, never both — so the
    // popover is one or the other, chosen by this mode (seeded from the value).
    const [mode, setMode] = React.useState(expressionValue ? "variable" : "date");
    // The expression draft is held in a ref (and owned by the expression editor), so
    // typing never re-renders this picker or the heavy calendar — it's read on save.
    const exprRef = useRef(expressionValue ?? "");
    const buttonRef = useRef(null);
    React.useEffect(() => {
        if (open) {
            exprRef.current = expressionValue ?? "";
            setMode(expressionValue ? "variable" : "date");
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
    const handleExprChange = React.useCallback((value) => {
        exprRef.current = value;
    }, []);
    // Sync draft value when popover opens or external value changes
    React.useEffect(() => {
        if (open) {
            setDraftValue(value);
            setMonth(value ?? new Date());
        }
    }, [open, value]);
    /**
     * carry over the current time when a user clicks a new day
     * instead of resetting to 00:00
     */
    const handleSelect = (newDay) => {
        if (!newDay)
            return;
        if (!draftValue) {
            setDraftValue(newDay);
            setMonth(newDay);
            return;
        }
        const diff = newDay.getTime() - draftValue.getTime();
        const diffInDays = diff / (1000 * 60 * 60 * 24);
        const newDateFull = add(draftValue, { days: Math.ceil(diffInDays) });
        setDraftValue(newDateFull);
        setMonth(newDateFull);
    };
    const handleSave = () => {
        // Commit whichever mode the user is in — the two are mutually exclusive.
        if (mode === "variable") {
            onExpressionChange?.(exprRef.current.trim());
            setOpen(false);
            // Mirror the date path so form-based callers (the filter) apply it.
            setTimeout(() => onSubmitDate?.(), 0);
            return;
        }
        onChange?.(draftValue);
        setOpen(false);
        // Delay form submission to ensure state update has propagated
        setTimeout(() => onSubmitDate?.(draftValue), 0);
    };
    const handleCancel = () => {
        setDraftValue(value);
        setOpen(false);
    };
    useImperativeHandle(ref, () => ({
        ...buttonRef.current,
        value,
    }), [value]);
    const initHourFormat = {
        hour24: displayFormat?.hour24 ??
            `PPP HH:mm${!granularity || granularity === "second" ? ":ss" : ""}`,
        hour12: displayFormat?.hour12 ??
            `PP hh:mm${!granularity || granularity === "second" ? ":ss" : ""} b`,
    };
    let loc = enUS;
    const { options, localize, formatLong } = locale;
    if (options && localize && formatLong) {
        loc = {
            ...enUS,
            options,
            localize,
            formatLong,
        };
    }
    const showClear = clearable && !!value && !disabled;
    return (<Popover open={open} onOpenChange={setOpen}>
        <div className="relative">
          <PopoverTrigger asChild disabled={disabled} ref={triggerRef}>
            <Button variant="outline" className={cn("w-full justify-start pl-3 text-left font-normal text-muted-foreground", !value && "text-muted-foreground", showClear && "pr-8", className)} ref={buttonRef}>
              <CalendarIcon className="mr-2 h-4 w-4"/>
              {expressionValue ? (<span className="truncate font-mono text-foreground">{expressionValue}</span>) : value ? (format(value, granularity === "day"
            ? "PPP"
            : hourCycle === 24
                ? initHourFormat.hour24
                : initHourFormat.hour12, {
            locale: loc,
        })) : (<span>{placeholder}</span>)}
            </Button>
          </PopoverTrigger>
          {showClear && (<Button variant="ghost" size="sm" type="button" className="absolute right-2 top-1/2 h-6 w-6 -translate-y-1/2 p-0 text-muted-foreground hover:text-destructive" onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange?.(undefined);
            }}>
              <X className="h-4 w-4"/>
            </Button>)}
        </div>
        <PopoverContent noPortal={noPortal} align="start" sideOffset={8} className="w-auto min-w-[var(--radix-popover-trigger-width)] p-0" onInteractOutside={(event) => {
            // The variable edit popup and the @-suggestion render outside this
            // content; interacting with them must not dismiss the date popover.
            const target = event.detail.originalEvent.target;
            if (target?.closest?.("[data-variable-edit-popup], .variable-suggestion-popup")) {
                event.preventDefault();
            }
        }}>
          {allowExpression && (<div className="flex gap-1 border-b border-border p-1.5">
              {["date", "variable"].map((m) => (<button key={m} type="button" onClick={() => setMode(m)} className={cn("flex-1 rounded px-2 py-1 text-xs font-medium capitalize transition-colors", mode === m
                    ? "bg-accent text-accent-foreground"
                    : "text-muted-foreground hover:bg-accent/50")}>
                  {m}
                </button>))}
            </div>)}
          {allowExpression && mode === "variable" ? (
        // Without the calendar there is nothing holding the popover open, so it
        // would collapse to the trigger width (tiny in the filter pill).
        <div className="min-w-[18rem] p-3">
              <div className="flex items-center rounded-md border border-input bg-background py-1 focus-within:ring-1 focus-within:ring-ring">
                <React.Fragment key={open ? "open" : "closed"}>
                  {renderExpressionEditor?.({
                initialValue: expressionValue ?? "",
                popupContainer: variablePopupContainer,
                onChange: handleExprChange,
                onCommit: handleSave,
            })}
                </React.Fragment>
              </div>
            </div>) : (<>
              <Calendar mode="single" selected={draftValue} month={month} onSelect={(d) => handleSelect(d)} onMonthChange={handleSelect} yearRange={yearRange} locale={locale} {...props}/>
              {granularity !== "day" && (<div className="border-t border-border p-3">
                  <TimePicker onChange={setDraftValue} date={draftValue} hourCycle={hourCycle} granularity={granularity}/>
                </div>)}
            </>)}
          <div className="flex justify-end gap-2 border-t border-border px-3 py-2">
            <Button variant="outline" size="sm" onClick={handleCancel}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleSave}>
              Save
            </Button>
          </div>
        </PopoverContent>
      </Popover>);
});
DateTimePicker.displayName = "DateTimePicker";
export { DayPicker, DateTimePicker, TimePickerInput, TimePicker, Calendar as DateTimeCalendar };
